import type { FormEvent } from "react";
import { useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatBytes } from "@bafe/validation";

import { Select } from "@bafe/ui";
import { DEFAULT_SITE_UPLOAD_MAX_BYTES, siteUploadLimitLabel, siteUploadTooLargeMessage } from "@bafe/types";

import { Popup } from "../../components/Popup";
import { api } from "../../lib/api";
import { presentError } from "../../lib/errors";
import { queryKeys } from "../../lib/query";

function folderCrumbs(hostname: string, path: string): Array<{ label: string; path: string }> {
	const crumbs = [{ label: hostname, path: "/" }];
	let current = "";
	for (const part of path.split("/").filter(Boolean)) {
		current += `/${part}`;
		crumbs.push({ label: part, path: current });
	}
	return crumbs;
}

export function SiteFiles() {
	const { siteId = "" } = useParams();
	const client = useQueryClient();
	const uploadRef = useRef<HTMLInputElement>(null);
	const [folder, setFolder] = useState("/");
	const [openPath, setOpenPath] = useState<string | null>(null);
	const [conflict, setConflict] = useState<"keep" | "replace">("keep");
	const [note, setNote] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [createOpen, setCreateOpen] = useState(false);
	const [createName, setCreateName] = useState("");
	const [createKind, setCreateKind] = useState<"file" | "directory">("file");
	const sites = useQuery({ queryKey: queryKeys.hostedSites, queryFn: () => api.listHostedSites() });
	const site = sites.data?.sites.find((item) => item.id === siteId);
	const files = useQuery({
		queryKey: [...queryKeys.hostedSites, siteId, "files", folder],
		queryFn: () => api.listSiteFiles(siteId, { path: folder }),
		enabled: siteId !== "",
	});
	const file = useQuery({
		queryKey: [...queryKeys.hostedSites, siteId, "file", openPath],
		queryFn: () => api.readSiteFile(siteId, openPath ?? ""),
		enabled: openPath !== null,
		retry: false,
	});
	const upload = useMutation({
		mutationFn: (chosen: File) =>
			api.uploadSiteFile(siteId, { path: folder, filename: chosen.name, file: chosen, conflict }),
		onSuccess: async (result) => {
			setNote(
				result.kept
					? "The existing file was kept."
					: result.replaced
						? "The existing file was replaced."
						: "The file was uploaded.",
			);
			setError(null);
			await client.invalidateQueries({ queryKey: [...queryKeys.hostedSites, siteId, "files"] });
		},
	});
	const createEntry = useMutation({
		mutationFn: (input: { name: string; kind: "file" | "directory" }) =>
			api.createSiteEntry(siteId, { path: folder, name: input.name, kind: input.kind }),
		onSuccess: async (created, input) => {
			setNote(input.kind === "directory" ? "The folder was created." : "The file was created.");
			setError(null);
			setCreateOpen(false);
			setCreateName("");
			setCreateKind("file");
			if (input.kind === "file") setOpenPath(created.path);
			await client.invalidateQueries({ queryKey: [...queryKeys.hostedSites, siteId, "files"] });
		},
	});
	const remove = useMutation({
		mutationFn: (target: string) => api.deleteSiteFile(siteId, target),
		onSuccess: async () => {
			setOpenPath(null);
			setNote("Deleted.");
			await client.invalidateQueries({ queryKey: [...queryKeys.hostedSites, siteId, "files"] });
		},
	});

	if (sites.isLoading || files.isLoading) return <p className="empty">Loading files.</p>;
	if (sites.isError) return <p className="err">{presentError(sites.error).title}</p>;
	if (!site) return <p className="err">We could not find that site.</p>;
	if (files.isError) return <p className="err">{presentError(files.error).title}</p>;
	const data = files.data;
	if (!data) return null;
	const maxUploadBytes = data.maxUploadBytes ?? DEFAULT_SITE_UPLOAD_MAX_BYTES;

	const crumbs = folderCrumbs(site.hostname, data.path);

	return (
		<div className="blocks">
			<p className="sub crumbs">
				<Link to="/site/sites">Sites</Link>
				{crumbs.map((crumb, index) => (
					<span key={crumb.path}>
						{" / "}
						{index === crumbs.length - 1 ? (
							<span className="here">{crumb.label}</span>
						) : (
							<button
								type="button"
								onClick={() => {
									setFolder(crumb.path);
									setOpenPath(null);
								}}
							>
								{crumb.label}
							</button>
						)}
					</span>
				))}
			</p>
			{!data.storageReady && <p className="err">Website storage is not configured on this server yet.</p>}
			{error && <p className="err">{error}</p>}
			{upload.isError && <p className="err">{presentError(upload.error).title}</p>}
			{createEntry.isError && <p className="err">{presentError(createEntry.error).title}</p>}
			{remove.isError && <p className="err">{presentError(remove.error).title}</p>}
			{note && <p className="sub">{note}</p>}
			<div className="file-bar">
				<div className="actions">
					<button
						className="btn"
						type="button"
						onClick={() => {
							setCreateOpen(true);
							setError(null);
						}}
					>
						New file or folder
					</button>
					<button
						className="btn"
						type="button"
						onClick={() => uploadRef.current?.click()}
						disabled={upload.isPending}
					>
						{upload.isPending ? "Uploading…" : "Upload"}
					</button>
					<input
						ref={uploadRef}
						type="file"
						hidden
						aria-label="Choose a file to upload"
						onChange={(event) => {
							const chosen = event.target.files?.[0];
							event.currentTarget.value = "";
							if (!chosen) return;
							if (chosen.size > maxUploadBytes) {
								setError(siteUploadTooLargeMessage(maxUploadBytes));
								setNote(null);
								return;
							}
							setError(null);
							upload.mutate(chosen);
						}}
					/>
				</div>
				<div className="file-opts">
					<label htmlFor="site-file-conflict">
						If the name exists
						<Select
							id="site-file-conflict"
							value={conflict}
							onChange={(event) => setConflict(event.target.value === "replace" ? "replace" : "keep")}
						>
							<option value="keep">Keep existing</option>
							<option value="replace">Replace existing</option>
						</Select>
					</label>
					<span>Up to {siteUploadLimitLabel(maxUploadBytes)}.</span>
				</div>
			</div>
			<Popup
				open={createOpen}
				title="New file or folder"
				onClose={() => {
					if (createEntry.isPending) return;
					setCreateOpen(false);
				}}
			>
				<form
					onSubmit={(event: FormEvent<HTMLFormElement>) => {
						event.preventDefault();
						const name = createName.trim();
						if (!name) return;
						createEntry.mutate({ name, kind: createKind });
					}}
				>
					<label htmlFor="site-entry-name">Name</label>
					<input
						id="site-entry-name"
						value={createName}
						onChange={(event) => setCreateName(event.target.value)}
						aria-label="New file or folder name"
						placeholder="index.html"
						autoComplete="off"
						autoFocus
						required
					/>
					<label htmlFor="site-entry-kind">Create</label>
					<Select
						id="site-entry-kind"
						value={createKind}
						aria-label="Create a file or folder"
						onChange={(event) => setCreateKind(event.target.value === "directory" ? "directory" : "file")}
					>
						<option value="file">Text file</option>
						<option value="directory">Folder</option>
					</Select>
					<p className="actions">
						<button className="btn" type="submit" disabled={createEntry.isPending}>
							{createEntry.isPending ? "Creating" : "Create"}
						</button>
						<button
							className="btn ghost"
							type="button"
							onClick={() => {
								if (createEntry.isPending) return;
								setCreateOpen(false);
							}}
						>
							Cancel
						</button>
					</p>
				</form>
			</Popup>
			<div className="table-wrap">
				<table>
					<caption className="sr-only">Files in {site.hostname}</caption>
					<thead>
						<tr>
							<th>Name</th>
							<th>Type</th>
							<th>Size</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{data.entries.length === 0 ? (
							<tr>
								<td colSpan={4} className="quiet">
									This folder is empty.
								</td>
							</tr>
						) : (
							data.entries.map((entry) => (
								<tr key={entry.path}>
									<td>
										{entry.type === "directory" ? (
											<button
												className="file-name"
												type="button"
												onClick={() => {
													setFolder(entry.path);
													setOpenPath(null);
												}}
											>
												{entry.name}/
											</button>
										) : (
											<button className="file-name" type="button" onClick={() => setOpenPath(entry.path)}>
												{entry.name}
											</button>
										)}
									</td>
									<td className="quiet">{entry.type === "directory" ? "Folder" : "File"}</td>
									<td className="quiet">{entry.size === null ? "—" : entry.size <= 0 ? "0 B" : formatBytes(entry.size)}</td>
									<td className="row-end">
										<button
											className="quiet-action"
											type="button"
											onClick={() => {
												if (
													window.confirm(
														entry.type === "directory"
															? `Delete ${entry.name} and everything inside it?`
															: `Delete ${entry.name}?`,
													)
												)
													remove.mutate(entry.path);
											}}
										>
											Delete
										</button>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
			{file.isError && <p className="err">{presentError(file.error).title}</p>}
			{file.data && (
				<form
					className="card"
					key={`${file.data.path}:${file.data.mtimeNs}:${file.data.size}`}
					onSubmit={(event) => {
						event.preventDefault();
						const content = new FormData(event.currentTarget).get("content");
						if (typeof content !== "string" || !file.data) return;
						const opened = file.data;
						setError(null);
						void api
							.writeSiteFile(siteId, opened.path, content, { mtimeNs: opened.mtimeNs, size: opened.size })
							.then(async () => {
								setNote("Saved.");
								await client.invalidateQueries({
									queryKey: [...queryKeys.hostedSites, siteId, "file", opened.path],
								});
								await client.invalidateQueries({ queryKey: [...queryKeys.hostedSites, siteId, "files"] });
							})
							.catch((cause: unknown) => setError(presentError(cause).title));
					}}
				>
					<h2>{file.data.path}</h2>
					{!file.data.editable && <p className="sub">This file cannot be edited as text.</p>}
					<label>
						Contents
						<textarea
							className="file-edit"
							name="content"
							defaultValue={file.data.content}
							readOnly={!file.data.editable}
							aria-label={`Contents of ${file.data.path}`}
						/>
					</label>
					{file.data.editable && (
						<button className="btn" type="submit">
							Save
						</button>
					)}
				</form>
			)}
		</div>
	);
}
